'use client'

import { useEffect, useState } from 'react'

/**
 * How many things are in the basket, for the badge in the header.
 *
 * The header is on every page, and the basket lives in a `HttpOnly` cookie, so
 * there are exactly two ways to get this number: read the cookie during render
 * — which turns fourteen prerendered marketing pages dynamic to draw one
 * integer — or fetch it from the browser. The same trade the layout already
 * makes for the session avatar, resolved the same way.
 *
 * `/api/koszyk` answers without touching Postgres when there is no cookie, so
 * the common case is a small invocation returning zero. If those invocations
 * ever matter, the answer is a second, NON-`HttpOnly` cookie carrying just the
 * count — fourteen bytes on every request, no round trip, no personal data —
 * not a page that reads the basket during render.
 *
 * The event is what keeps the badge honest on the one page that can change the
 * number without a navigation.
 */
export const CART_EVENT = 'flou:koszyk'

export function useCartCount(): number {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const controller = new AbortController()

    fetch('/api/koszyk', { cache: 'no-store', signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: unknown) => {
        const value = (data as { count?: unknown } | null)?.count
        if (typeof value === 'number') setCount(value)
      })
      // An unreachable basket count is not worth a console error on every page.
      .catch(() => {})

    const onChange = (event: Event) => {
      if (event instanceof CustomEvent && typeof event.detail === 'number') setCount(event.detail)
    }
    window.addEventListener(CART_EVENT, onChange)

    return () => {
      controller.abort()
      window.removeEventListener(CART_EVENT, onChange)
    }
  }, [])

  return count
}
